// Deal Contacts panel — multiple contacts per deal with buying-role + primary flag.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Star, Trash2, Mail, Phone } from "lucide-react";
import { cn } from "@/lib/cn";

const ROLES = [
  "DECISION_MAKER", "CHAMPION", "ECONOMIC_BUYER", "TECHNICAL_BUYER",
  "INFLUENCER", "USER", "GATEKEEPER", "EVALUATOR", "OTHER",
] as const;

interface Props { dealId: string }

export function DealContactsPanel({ dealId }: Props) {
  const qc = useQueryClient();
  const queryKey = ["/deals", dealId, "contacts"];
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/deals/${dealId}/contacts`)).data.data as any[],
  });

  const rows = data ?? [];

  async function updateLink(linkId: string, patch: Record<string, unknown>) {
    await api.put(`/deals/${dealId}/contacts/${linkId}`, patch);
    qc.invalidateQueries({ queryKey });
  }

  async function remove(linkId: string) {
    if (!confirm("Remove this contact from the deal?")) return;
    await api.delete(`/deals/${dealId}/contacts/${linkId}`);
    qc.invalidateQueries({ queryKey });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {rows.length} contact{rows.length === 1 ? "" : "s"} on this deal
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4"/>Add contact
        </Button>
      </div>

      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No contacts on this deal yet. Add a Decision Maker, Champion, or Influencer to get started.
        </p>
      )}

      {rows.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr>
                <Th className="w-8"></Th>
                <Th>Contact</Th>
                <Th>Account</Th>
                <Th>Role</Th>
                <Th>Email</Th>
                <Th className="w-24"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => {
                const c = r.contact;
                return (
                  <Tr key={r.id} className="group">
                    <Td>
                      <button
                        onClick={() => updateLink(r.id, { isPrimary: !r.isPrimary })}
                        title={r.isPrimary ? "Primary contact" : "Set as primary"}
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded transition-colors",
                          r.isPrimary ? "text-amber-500" : "text-muted-foreground/40 hover:text-amber-500",
                        )}
                      >
                        <Star className={cn("h-4 w-4", r.isPrimary && "fill-current")} />
                      </button>
                    </Td>
                    <Td>
                      <Link to={`/contacts/${c?.id}`} className="font-medium hover:underline">
                        {c?.name ?? "—"}
                      </Link>
                      {c?.title && <div className="text-xs text-muted-foreground">{c.title}</div>}
                    </Td>
                    <Td className="text-muted-foreground">
                      {c?.account?.name ? <Link to={`/accounts/${c.account.id}`} className="hover:underline">{c.account.name}</Link> : "—"}
                    </Td>
                    <Td>
                      <Select
                        value={r.role}
                        onChange={(e) => updateLink(r.id, { role: e.target.value })}
                        className="h-7 w-44 text-xs"
                      >
                        {ROLES.map((role) => <option key={role} value={role}>{role.replace(/_/g, " ")}</option>)}
                      </Select>
                    </Td>
                    <Td className="text-muted-foreground text-sm">{c?.email ?? "—"}</Td>
                    <Td>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                        {c?.email && (
                          <a href={`mailto:${c.email}`} title="Email" className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted">
                            <Mail className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        {c?.phone && (
                          <a href={`tel:${c.phone}`} title="Call" className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted">
                            <Phone className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        <button onClick={() => remove(r.id)} title="Remove" className="flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-muted">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </div>
      )}

      {/* Role distribution */}
      {rows.length > 0 && (
        <div className="rounded-md border p-3 text-xs">
          <div className="mb-2 font-medium">Buying committee</div>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((role) => {
              const n = rows.filter((r: any) => r.role === role).length;
              if (n === 0) return null;
              return <Badge key={role} variant="secondary">{role.replace(/_/g, " ")}: {n}</Badge>;
            })}
          </div>
        </div>
      )}

      <AddContactModal open={addOpen} onClose={() => setAddOpen(false)} dealId={dealId} />
    </div>
  );
}

function AddContactModal({ open, onClose, dealId }: { open: boolean; onClose: () => void; dealId: string }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [contactId, setContactId] = useState("");
  const [role, setRole] = useState<typeof ROLES[number]>("OTHER");
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["picker-contacts", q],
    queryFn: async () => (await api.get("/contacts", { params: { q, pageSize: 25 } })).data.data as any[],
    enabled: open,
  });

  const rows = useMemo(() => data ?? [], [data]);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/deals/${dealId}/contacts`, { contactId, role, isPrimary });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/deals", dealId, "contacts"] });
      setContactId(""); setRole("OTHER"); setIsPrimary(false); setQ(""); setError(null); onClose();
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to add contact."),
  });

  return (
    <BulkModal
      open={open}
      onClose={() => { setContactId(""); setError(null); onClose(); }}
      title="Add contact to deal"
      description="Pick a contact and assign their buying role on this deal."
      confirmLabel="Add to deal"
      confirmDisabled={!contactId}
      busy={mut.isPending}
      onConfirm={() => mut.mutate()}
      error={error}
    >
      <div>
        <Label>Search contacts</Label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name or email…" />
      </div>
      <div className="max-h-64 overflow-auto rounded-md border">
        {isFetching && <p className="p-3 text-sm text-muted-foreground">Loading…</p>}
        {!isFetching && rows.length === 0 && <p className="p-3 text-sm text-muted-foreground">No matches.</p>}
        {rows.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input type="radio" name="contact" checked={contactId === c.id} onChange={() => setContactId(c.id)} />
            <span className="font-medium">{c.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">{c.title ?? c.account?.name ?? c.email ?? ""}</span>
          </label>
        ))}
      </div>
      <div>
        <Label htmlFor="role">Role</Label>
        <Select id="role" value={role} onChange={(e) => setRole(e.target.value as any)}>
          {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <input id="isPrimary" type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
        <Label htmlFor="isPrimary">Set as primary contact</Label>
      </div>
    </BulkModal>
  );
}
